import React from "react";
import { Tooltip } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useAuth } from "providers/Auth";
import { everyone } from "config/permissionDefs";

interface SigilUiGridButtonProps
  extends React.ComponentProps<typeof LoadingButton> {
  toolTipTitle?: string;
  minMembership?: number;
  minMembershipMessage?: string;
  label?: string;
  eventCategory?: string;
  styles?: React.CSSProperties;
  component?: React.ElementType;
  eventLabel: string;
}

const SigilUiGridButton: React.FC<SigilUiGridButtonProps> = ({
  children,
  toolTipTitle,
  minMembership = everyone,
  minMembershipMessage = "You must upgrade your membership to use this feature.",
  label,
  eventCategory = "button_click",
  eventLabel,
  component: ButtonComponent = LoadingButton,
  ...props
}) => {
  const authContext = useAuth()!;

  const { hasValidMembership } = authContext;
  const analytics = getAnalytics();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    logEvent(analytics, eventCategory, {
      event_label: eventLabel || "unknown_button",
    });

    if (props.onClick) {
      props.onClick(event);
    }
  };

  const sharedStyles = {
    fontWeight: 600,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  };

  if (hasValidMembership(minMembership)) {
    return (
      <Tooltip title={toolTipTitle} placement="bottom">
        <ButtonComponent
          size="small"
          color="secondary"
          variant="contained"
          sx={{ ...sharedStyles, ...(props.styles || {}) }}
          {...props}
          onClick={handleClick}
        >
          {label}
        </ButtonComponent>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title={minMembershipMessage} placement="bottom">
        <span>
          <ButtonComponent
            size="small"
            color="secondary"
            variant="contained"
            disabled
            sx={{ ...sharedStyles, ...(props.styles || {}) }}
            {...props}
          >
            {label}
          </ButtonComponent>
        </span>
      </Tooltip>
    );
  }
};

export default SigilUiGridButton;
